import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Api } from '../../api';
import {
  errorToast,
  successToast,
  useOutsideClick,
  getFilterKey,
  capitalizeFirstLetter
} from '../../utils/helper';
import Breadcrumb from '../../component/common/Breadcrumb';
import Table from '../../component/common/Table';
import Pagination from '../../component/common/Pagination/Pagination';
import { Helmet } from 'react-helmet';
import SearchInput from '../../component/common/Input/SearchInput';
import { CONTENT_TYPE_FOR_FEEDBACKS, PER_PAGE } from '../../utils/constants';
import { Fragment } from 'react';
import { FunnelIcon } from '@heroicons/react/24/outline';
import { Transition } from '@headlessui/react';
import SelectMenu from '../../component/common/SelectMenu';
import PropTypes from 'prop-types';
import Loader from '../../component/common/Loader';
import Topbar from '../PulseSurvey/Topbar';
import { AppFeedBackPageData as pageData } from '../../utils/pageContent';
import { FilterPanel, TableCard} from '../../component/common/designSystem';

const pages = [{ name: pageData.pageTitle, href: '/app-feedbacks' }];

const PAGE_SIZE = 10;
const INITIAL_FILTER = {
  contentType: structuredClone(CONTENT_TYPE_FOR_FEEDBACKS[0])
};
const INITIAL_QUERY = `&contentType=${INITIAL_FILTER.contentType.value}`;

const AppFeedbacks = (props) => {
  const wrapperRef = useRef(null);
  const [loader, setLoader] = useState(true);
  const [feedbacksList, setFeedbacksList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(-1);
  const [sortBy, setSortBy] = useState('');
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [columns, setColumns] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState(INITIAL_FILTER);
  const [filterQuery, setFilterQuery] = useState(INITIAL_QUERY);

  const prepareColumnsFromFeedbacksData = (data) => {
    const columns = Object.keys(data?.[0]?.feedback || {}).map((feedback) => {
      const title = capitalizeFirstLetter(feedback);
      return {
        title,
        key: feedback,
        sortable: true,
        type: 'feedback',
        align: 'left',
        width: 100,
        sortKey: feedback
      };
    });
    columns.unshift({
      title: pageData.contentNameText,
      sortable: true,
      longText: true,
      key: 'contentName',
      sortKey: 'display_name',
      width: 300,
      type: 'text',
      align: 'left'
    });
    setColumns(columns);
  };

  const handlePagination = (pageNumber, pageSize, searchKey, sortBy, sortOrder) => {
    setLoader(true);
    console.log({ pageNumber, pageSize, searchKey, sortBy, sortOrder }, 'paginallll;');

    Api.getFeedbacksList(pageNumber, pageSize, searchKey, filterQuery, sortBy, sortOrder).then(
      (response) => {
        if (response?.data?.meta?.code === 1) {
          setCurrentPage(pageNumber);
          prepareColumnsFromFeedbacksData(response?.data?.data || []);
          setFeedbacksList(response?.data?.data);
          setTotalCount(response?.data?.meta?.totalRecords);
          setLoader(false);
        } else if (response?.data?.meta?.code === 0) {
          setCurrentPage(1);
          setFeedbacksList([]);
          setTotalCount(0);
          setLoader(false);
          errorToast(response?.data?.meta?.message);
        } else {
          setFeedbacksList([]);
          setLoader(false);
        }
      }
    );
  };
  useEffect(() => {
    if (searchTerm) {
      let searchParam = searchTerm.trim();
      const delayDebounceFn = setTimeout(() => {
        handlePagination(1, PAGE_SIZE, searchParam, sortBy, sortOrder);
      }, 800);

      return () => clearTimeout(delayDebounceFn);
    } else {
      handlePagination(1, PAGE_SIZE, '', sortBy, sortOrder);
    }
  }, [searchTerm]);

  const deleteHandler = (id) => {
    setLoader(true);
    setSearchTerm('');
    Api.deleteMeditation(id).then((response) => {
      if (response?.data?.meta?.code === 1) {
        handlePagination(1, PAGE_SIZE, '', sortBy, sortOrder);
        successToast(response?.data?.meta?.message);
      } else if (response?.data?.meta?.code === 0) {
        setLoader(false);
        errorToast(response?.data?.meta?.message);
      } else {
        setLoader(false);
      }
    });
  };
  const refreshTable = () => handlePagination(1, PAGE_SIZE, '', sortBy, sortOrder);
  const handleSortBy = (sortByValue) => {
    setSortBy(sortByValue);
    if (sortByValue === sortBy) {
      let tempSortOrder = sortOrder === 1 ? -1 : 1;
      setSortOrder(tempSortOrder);
      handlePagination(currentPage, PAGE_SIZE, searchTerm, sortByValue, tempSortOrder);
    } else {
      setSortOrder(1);
      handlePagination(currentPage, PAGE_SIZE, searchTerm, sortByValue, 1);
    }
  };
  useOutsideClick(wrapperRef, () => {
    if (showFilterModal) setShowFilterModal(!showFilterModal);
  });

  const filterHandler = (data, key) => {
    setFilterCriteria((prevData) => ({
      ...prevData,
      [key]: data
    }));
    let clone = JSON.parse(JSON.stringify(filterCriteria));
    const newData = { ...clone, [key]: data };
    let tempFilterQuery = [];
    Object.keys(newData).forEach(function (key) {
      if (newData[key]?.value?.toString()) {
        tempFilterQuery.push(`&${key}=${newData[key]?.value}`);
      }
    });
    let query = tempFilterQuery?.toString()?.replaceAll(',', '');
    setFilterQuery(query);
  };

  useEffect(() => {
    if (filterQuery) {
      handlePagination(1, selectedPerPage.value, searchTerm, sortBy, sortOrder);
    }
  }, [filterQuery]);

  const resetHandler = () => {
    setFilterCriteria(INITIAL_FILTER);
    setFilterQuery(INITIAL_QUERY);
    setShowFilterModal(false);
    handlePagination(1, selectedPerPage.value, searchTerm, sortBy, sortOrder);
  };

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    handlePagination(1, perPage.value, searchTerm, sortBy, sortOrder);
  };

  useOutsideClick(wrapperRef, () => {
    if (showFilterModal) setShowFilterModal(!showFilterModal);
  });

  return (
    <div className="relative lg:mb-[45px]">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageData.metaTitle}</title>
      </Helmet>
      {loader ? <Loader /> : null}
      <Topbar title={pageData.pageTitle} />
      <Breadcrumb pageList={pages} className="lg:mt-8" />
      <div className="mt-4">
        <div className="sm:flex justify-between">
          <div className="flex">
            <div className="hidden sm:flex mr-3 self-center">
              <span className="self-center text-sm mr-2 text-shoorah-lightBlack dark:text-white">{pageData.perPageText}</span>
              <div className="w-[80px]">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="self-center">
              <SearchInput
                id="searchKey"
                name="searchKey"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={pageData.searchPlaceholder}
              />
            </div>
          </div>
          <div className="flex justify-around items-center">
            <div className="sm:hidden flex self-center">
              <span className="self-center hidden text-sm mr-2 dark:text-white">{pageData.perPageText}</span>
              <div className="w-16">
                <SelectMenu
                  menuList={PER_PAGE}
                  showLabel={false}
                  defaultSelected={selectedPerPage}
                  setSelectedMenu={handlePerPage}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-y-2">
              <div className="ml-3 self-center">
                <button
                  className="btn-primary sm:w-auto"
                  onClick={() => setShowFilterModal((state) => !state)}
                >
                  <FunnelIcon className="text-white w-[18px] h-[20px] inline" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <FilterPanel
          open={showFilterModal}
          onClose={() => setShowFilterModal(false)}
          onReset={resetHandler}
          columns={1}
        >
              <div>
                <SelectMenu
                  menuList={structuredClone(CONTENT_TYPE_FOR_FEEDBACKS)}
                  defaultSelected={filterCriteria.contentType}
                  label={pageData.contentTypeLabel}
                  isRequired={false}
                  setSelectedMenu={(data) => filterHandler(data, 'contentType')}
                />
              </div>
            </FilterPanel>

        {
          <div className="border dark:border-shoorah-darkBgTabColor py-1 px-2 mt-3">
            <div className="flex justify-between">
              <div className="self-center overflow-auto filter-container mr-2">
                <div className="flex">
                  {Object.keys(filterCriteria).map((keyName, i) => (
                    <>
                      {filterCriteria?.[keyName]?.value?.toString() ? (
                        <div
                          className="flex bg-gray-200 dark:bg-shoorah-darkBgTabColor px-2 py-1 mr-2 whitespace-nowrap"
                          key={i}
                        >
                          <p className="text-[14px] object-contain m-0 whitespace-nowrap">
                            {getFilterKey(keyName)}
                          </p>
                          <p className="text-[14px] m-0 whitespace-nowrap">&nbsp;: &nbsp;</p>
                          <p className="text-[14px] m-0 whitespace-nowrap ">
                            {filterCriteria?.[keyName].name}
                          </p>
                        </div>
                      ) : (
                        ''
                      )}
                    </>
                  ))}
                </div>
              </div>
            </div>
          </div>
        }
        <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: (page) =>
              handlePagination(
                page,
                selectedPerPage?.value,
                searchTerm,
                // filterQuery,
                sortBy,
                sortOrder
              ),
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
            columns={columns}
            data={feedbacksList}
            name={'feedbacks_table'}
            setDeleteId={deleteHandler}
            bottomBorder={totalCount > PAGE_SIZE}
            setSortBy={(sort) => handleSortBy(sort)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            contentType={3}
            refreshTable={refreshTable}
            loader={loader}
            setSearchTerm={(data) => setSearchTerm(data)}
          />
        </TableCard>
      </div>
      
    </div>
  );
};

AppFeedbacks.propTypes = {
  location: PropTypes.any
};

export default AppFeedbacks;
